import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, RefreshCw, Search, X, ChevronLeft, ChevronRight, Pin } from 'lucide-react';
import ArticleBadge from '../components/ArticleBadge';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import TopNav from '../components/sections/TopNav';
import ShareButtons from '../components/ShareButtons';
import AdSlot from '../components/AdSlot';
import { useJsonLd, buildStoriesItemList, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const StoryCard = ({ story, featured = false }) => {
  const date = story.published_at
    ? (() => {
        const d = new Date(story.published_at);
        if (Number.isNaN(d.getTime())) return story.published_at;
        const datePart = d.toLocaleDateString('en-AU', {
          day: 'numeric', month: 'short', year: 'numeric',
          timeZone: 'Australia/Melbourne',
        });
        const timePart = d.toLocaleTimeString('en-AU', {
          hour: 'numeric', minute: '2-digit', hour12: true,
          timeZone: 'Australia/Melbourne',
        });
        return `${datePart} · ${timePart.toLowerCase()}`;
      })()
    : '';
  const href = `/stories/${story.slug}`;
  const containerClass = featured
    ? 'group block bg-zinc-900/70 border border-yellow-500/60 hover:border-yellow-400 rounded-lg overflow-hidden transition-colors duration-150 relative'
    : 'group block bg-zinc-900/70 border border-zinc-800 hover:border-red-500/50 rounded-lg overflow-hidden transition-colors duration-150 relative';

  return (
    <div
      className={containerClass}
      data-testid={featured ? 'story-card-featured' : 'story-card'}
    >
      {featured && (
        <div
          className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center gap-1.5 bg-yellow-500 text-black text-[10px] font-black uppercase tracking-[0.25em] py-1.5"
          data-testid="pinned-strip"
        >
          <Pin className="h-3 w-3" strokeWidth={3} /> Editor&apos;s Pick
        </div>
      )}
      <Link to={href} className={`block ${featured ? 'pt-7' : ''}`}>
        {story.cover_image_url && (
          <div className="aspect-video bg-zinc-950 overflow-hidden relative">
            <img
              src={story.cover_image_url}
              alt={story.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
            />
            <div className="absolute top-2 left-2">
              <ArticleBadge article={story} size="md" testId="story-card-badge" />
            </div>
          </div>
        )}
        <div className="p-4 md:p-5 pr-12">
          <p className="text-[11px] text-red-400 font-bold uppercase tracking-widest mb-2">
            {date}
          </p>
          <h3 className={`font-black text-white mb-2 leading-snug group-hover:text-red-400 transition-colors ${featured ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'}`}>
            {story.title}
          </h3>
          <p className={`text-gray-400 leading-relaxed ${featured ? 'text-base line-clamp-4' : 'text-sm line-clamp-3'}`}>
            {story.excerpt || story.subtitle}
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-red-400 font-bold uppercase tracking-wider">
            Read full story <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </Link>
      <div className="absolute bottom-3 right-3 z-10">
        <ShareButtons
          url={typeof window !== 'undefined' ? `${window.location.origin}${href}` : href}
          title={story.title}
          text={story.excerpt || story.subtitle || ''}
          testId={`story-share-${story.slug?.slice(0, 20)}`}
        />
      </div>
    </div>
  );
};

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [page, setPage] = useState(1); // 1-indexed
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useJsonLd(buildStoriesItemList(stories), 'stories-list');
  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'Investigations', url: 'https://www.outlawmedia.com.au/stories' },
  ]), 'stories-breadcrumb');

  // Debounce search → 350ms after typing stops
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 whenever the active search query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const load = useCallback(async (targetPage, searchTerm) => {
    setLoading(true);
    setError('');
    try {
      const offset = (targetPage - 1) * PAGE_SIZE;
      const params = new URLSearchParams({
        offset: String(offset),
        limit: String(PAGE_SIZE),
        _ts: String(Date.now()),
      });
      if (searchTerm) params.set('q', searchTerm);
      const res = await fetch(`${API}/api/articles?${params}`, {
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('Could not load stories.');
      const data = await res.json();
      setStories(data.items || []);
      setHasMore(Boolean(data.has_more));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Could not load stories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, debouncedQuery);
  }, [load, page, debouncedQuery]);

  // No client-side filter anymore - the archive search runs server-side
  const visibleStories = stories;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="stories-page">
      <TopNav />
      <PageSeoTag pageKey="stories" />
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold" data-testid="stories-back-home">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <span className="text-sm font-black tracking-widest text-red-400">INVESTIGATIONS</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
            Investigations
          </h1>
          <p className="text-sm text-gray-400 max-w-2xl">
            Every deep-dive, leak, and breaking story from Outlaw Media - published direct,
            no gatekeepers.
          </p>

          {/* Search bar */}
          <div className="mt-6 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search investigations…"
              data-testid="stories-search-input"
              className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-red-500 text-white pl-9 pr-9 py-2.5 rounded-md text-sm focus:outline-none placeholder-gray-600"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                data-testid="stories-search-clear"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full text-gray-500 hover:text-white hover:bg-zinc-800 flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="text-center py-16" data-testid="stories-loading">
            <RefreshCw className="h-8 w-8 text-red-500 animate-spin mx-auto mb-3" />
            <p className="text-gray-400">Loading stories…</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16" data-testid="stories-error">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => load(page, debouncedQuery)}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-bold text-sm"
            >
              Retry
            </button>
            <p className="text-xs text-gray-500 mt-4">
              If this keeps happening, please email{' '}
              <a href="mailto:ryan@outlawmedia.com.au" className="text-red-400 underline">ryan@outlawmedia.com.au</a>
            </p>
          </div>
        )}

        {!loading && !error && (
          <>
            {debouncedQuery && (
              <p className="text-xs text-gray-500 mb-4 text-center" data-testid="stories-search-status">
                {visibleStories.length === 0
                  ? `No matches for "${debouncedQuery}" across the full archive.`
                  : `Showing matches for "${debouncedQuery}" - page ${page}${hasMore ? '' : ' (last)'}`}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stories-grid">
              {visibleStories.map((s, idx) => (
                <React.Fragment key={s.id || s.slug || s.link}>
                  <StoryCard
                    story={s}
                    featured={
                      // Only highlight the pinned card on the first page and
                      // when no search filter is active - otherwise the "pin"
                      // context makes no sense.
                      Boolean(s.pinned) && page === 1 && !debouncedQuery
                    }
                  />
                  {/* Inline ad every 6 stories - full-width across the grid.
                      6 aligns cleanly with the 3-column desktop grid so each
                      "section" is exactly 2 full rows before the ad break. */}
                  {((idx + 1) % 6 === 0) && (idx < visibleStories.length - 1) && (
                    <div className="sm:col-span-2 lg:col-span-3 -mx-4 sm:mx-0">
                      <AdSlot
                        slotId={`stories-inline-${Math.floor(idx / 6)}`}
                        className="py-2"
                        aspect="aspect-[5/1]"
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Pagination - works for both browsing & search */}
            {(visibleStories.length > 0) && (page > 1 || hasMore) && (
              <div
                className="mt-10 flex items-center justify-center gap-3"
                data-testid="stories-pagination"
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  data-testid="stories-prev-btn"
                  className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed border border-red-900/40 text-gray-200 font-bold text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <span
                  className="text-xs font-black tracking-[0.2em] text-red-400 uppercase px-3 py-2 rounded-lg bg-red-600/10 border border-red-600/30"
                  data-testid="stories-page-indicator"
                >
                  Page {page}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasMore || loading}
                  data-testid="stories-next-btn"
                  className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed text-white font-bold text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default Stories;
